/**
 * Authentication Service
 * 
 * Handles all authentication-related API calls to the backend.
 * Endpoints: /api/auth/* (proxied via nginx)
 */

const API_BASE = ''; // Same-origin requests

export const authService = {
    /**
     * Register a new user
     */
    async register(email, password) {
        try {
            const response = await fetch('/api/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, password })
            });
            return { ok: response.ok, status: response.status };
        } catch (error) {
            console.error('Register error:', error);
            return { ok: false, status: null, error: error.message };
        }
    },

    /**
     * Login an existing user
     */
    async login(email, password) {
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, password })
            });
            return { ok: response.ok, status: response.status };
        } catch (error) {
            console.error('Login error:', error);
            return { ok: false, status: null, error: error.message };
        }
    },

    /**
     * Logout the current user
     */
    async logout() {
        try {
            await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include'
            });
            return true;
        } catch (error) {
            console.error('Logout error:', error);
            return false;
        }
    },

    /**
     * Get current user info
     */
    async me() {
        try {
            const response = await fetch('/api/auth/me', {
                credentials: 'include'
            });
            if (!response.ok) {
                return null;
            }
            return await response.json();
        } catch (error) {
            console.error('Me error:', error);
            return null;
        }
    },

    /**
     * Verify email with token
     */
    async verifyEmail(token) {
        try {
            const response = await fetch('/api/auth/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ token })
            });
            return { ok: response.ok, status: response.status };
        } catch (error) {
            console.error('Verify email error:', error);
            return { ok: false, status: null, error: error.message };
        }
    },

    /**
     * Resend verification email
     */
    async resendVerification(email) {
        try {
            const response = await fetch('/api/auth/resend', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email })
            });
            return { ok: response.ok, status: response.status };
        } catch (error) {
            console.error('Resend verification error:', error);
            return { ok: false, status: null, error: error.message };
        }
    },

    /**
     * Admin-only: Create a new user account
     */
    async adminCreateUser(email, password) {
        try {
            const response = await fetch('/api/admin/create-user', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, password })
            });
            return { ok: response.ok, status: response.status };
        } catch (error) {
            console.error('Admin create user error:', error);
            return { ok: false, status: null, error: error.message };
        }
    },

    /**
     * Admin-only: Send invitation to a new user
     */
    async adminInviteUser(email, org, contractAddress, role, notes) {
        try {
            const response = await fetch('/api/admin/invite-user', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    email,
                    org,
                    contract_address: contractAddress,
                    role,
                    notes
                })
            });
            return { ok: response.ok, status: response.status };
        } catch (error) {
            console.error('Admin invite user error:', error);
            return { ok: false, status: null, error: error.message };
        }
    },

    /**
     * Get invitation details by token
     */
    async getInvitation(token) {
        try {
            const response = await fetch(`/api/auth/invitation/${token}`, {
                credentials: 'include'
            });
            if (!response.ok) {
                return { ok: false, status: response.status };
            }
            const data = await response.json();
            return { ok: true, data };
        } catch (error) {
            console.error('Get invitation error:', error);
            return { ok: false, error: error.message };
        }
    },

    /**
     * Accept invitation and create account
     */
    async acceptInvitation(token, password) {
        try {
            const response = await fetch('/api/auth/accept-invitation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ token, password })
            });
            return { ok: response.ok, status: response.status };
        } catch (error) {
            console.error('Accept invitation error:', error);
            return { ok: false, status: null, error: error.message };
        }
    },

    /**
     * Get user's assigned contracts
     */
    async getUserContracts() {
        try {
            const response = await fetch('/api/v1/customer/contracts', {
                credentials: 'include'
            });
            if (!response.ok) {
                return { ok: false, status: response.status };
            }
            const data = await response.json();
            return { ok: true, data };
        } catch (error) {
            console.error('Get user contracts error:', error);
            return { ok: false, error: error.message };
        }
    },

    /**
     * Get customer dashboard data
     */
    async getCustomerDashboard() {
        try {
            const response = await fetch('/api/v1/customer/dashboard', {
                credentials: 'include'
            });
            if (!response.ok) {
                return { ok: false, status: response.status };
            }
            const data = await response.json();
            return { ok: true, data };
        } catch (error) {
            console.error('Get customer dashboard error:', error);
            return { ok: false, error: error.message };
        }
    },

    /**
     * Admin-only: Get all users with their details
     */
    async adminGetUsers() {
        try {
            const response = await fetch('/api/admin/users', {
                credentials: 'include'
            });
            if (!response.ok) {
                return { ok: false, status: response.status };
            }
            const data = await response.json();
            return { ok: true, data };
        } catch (error) {
            console.error('Admin get users error:', error);
            return { ok: false, error: error.message };
        }
    },

    /**
     * Admin-only: Get pending invitations
     */
    async adminGetInvitations() {
        try {
            const response = await fetch('/api/admin/invitations', {
                credentials: 'include'
            });
            if (!response.ok) {
                return { ok: false, status: response.status };
            }
            const data = await response.json();
            return { ok: true, data };
        } catch (error) {
            console.error('Admin get invitations error:', error);
            return { ok: false, error: error.message };
        }
    },

    /**
     * Admin-only: Get a specific user's dashboard data
     */
    async adminGetUserDashboard(userId) {
        try {
            const response = await fetch(`/api/admin/users/${userId}/dashboard`, {
                credentials: 'include'
            });
            if (!response.ok) {
                return { ok: false, status: response.status };
            }
            const data = await response.json();
            return { ok: true, data };
        } catch (error) {
            console.error('Admin get user dashboard error:', error);
            return { ok: false, error: error.message };
        }
    },

    /**
     * Admin-only: Update a user's account information
     */
    async adminUpdateUser(userId, userData) {
        try {
            const response = await fetch(`/api/admin/users/${userId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userData)
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                return { ok: false, status: response.status, error: errorData.error || 'Failed to update user' };
            }
            const data = await response.json();
            return { ok: true, data };
        } catch (error) {
            console.error('Admin update user error:', error);
            return { ok: false, error: error.message };
        }
    }
};

